import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { CheckCircle, X, ArrowRight, Info, Plus } from '@phosphor-icons/react';
import MarketingNav from '../../components/marketing/MarketingNav';
import MarketingFooter from '../../components/marketing/MarketingFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Public pricing page. Reads plans + add-ons from Mongo via `/api/plans` and
 * `/api/addons` so the super-admin can retune pricing without a redeploy.
 *
 * Highlights of this v2 render:
 *  - Billing toggle: Monthly / Annual (annual applies plan.annual_discount_pct)
 *  - Currency toggle: USD (default) / INR (approx conversion for display only)
 *  - Enterprise tier with monthly_price=0 renders as "Custom" with a sales CTA
 *  - A dedicated Add-ons section below the main tiers, since any plan can
 *    bolt them on (yearly only).
 */

// INR conversion rate for display only. We don't quote real-time FX on a
// marketing page; this is a stable "roughly ₹83/$1" multiplier that keeps the
// INR column meaningful. Super-admin can edit plan.monthly_price directly if
// they ever want to lock INR pricing exactly.
const USD_TO_INR = 83;

export default function Pricing() {
  const [annual, setAnnual] = useState(true);
  const [currency, setCurrency] = useState('USD'); // 'USD' | 'INR'
  const [plans, setPlans] = useState([]);
  const [addons, setAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Pricing — Vooxy';
    const stamp = Date.now();
    Promise.all([
      axios.get(`${API}/plans?_=${stamp}`, { headers: { 'Cache-Control': 'no-cache' } }),
      axios.get(`${API}/addons?_=${stamp}`, { headers: { 'Cache-Control': 'no-cache' } }).catch(() => ({ data: { addons: [] } })),
    ])
      .then(([pRes, aRes]) => {
        setPlans((pRes.data.plans || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
        setAddons((aRes.data.addons || []).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
      })
      .catch(() => setError('Pricing details are temporarily unavailable. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  const fmtMoney = (usd) => {
    if (currency === 'INR') {
      const inr = Math.round(usd * USD_TO_INR);
      return `₹${inr.toLocaleString('en-IN')}`;
    }
    return `$${usd}`;
  };

  const price = (t) => {
    const m = t.monthly_price || 0;
    // monthly_price=0 is our marker for "Custom quote" (Enterprise tier)
    if (m === 0) {
      return { display: 'Custom', sub: 'Tailored to your scale — talk to sales', isCustom: true };
    }
    if (annual) {
      const yearly = Math.round(m * (1 - (t.annual_discount_pct || 0) / 100));
      return {
        display: fmtMoney(yearly),
        sub: `per user / month · billed annually`,
        isCustom: false,
      };
    }
    return {
      display: fmtMoney(m),
      sub: `per user / month · billed monthly`,
      isCustom: false,
    };
  };

  const gridCols =
    plans.length >= 5 ? 'lg:grid-cols-5' :
    plans.length === 4 ? 'lg:grid-cols-4' :
    plans.length === 3 ? 'lg:grid-cols-3' :
    plans.length === 2 ? 'lg:grid-cols-2' : 'lg:grid-cols-1';

  return (
    <div className="min-h-screen bg-[#050B18] text-white antialiased" data-testid="marketing-pricing">
      <MarketingNav />

      {/* Header */}
      <section className="pt-32 lg:pt-40 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] rounded-full bg-emerald-500/[0.06] blur-[120px]" />
        </div>
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-4">Pricing</div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.05] mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Simple pricing.{' '}
            <span className="text-emerald-400">Serious value.</span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-10">
            Every plan includes multi-tenant isolation, audit logging, and unlimited leads. Pay only per active user — cancel anytime. Pick a plan, bolt on exactly the modules you need.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4">
            {/* Billing toggle */}
            <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white/[0.03] border border-white/10">
              <button
                onClick={() => setAnnual(false)}
                data-testid="billing-toggle-monthly"
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  !annual ? 'bg-emerald-500 text-[#050B18]' : 'text-slate-300 hover:text-white'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setAnnual(true)}
                data-testid="billing-toggle-annual"
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  annual ? 'bg-emerald-500 text-[#050B18]' : 'text-slate-300 hover:text-white'
                }`}
              >
                Annual
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                  annual ? 'bg-[#050B18]/30 text-[#050B18]' : 'bg-emerald-500/20 text-emerald-300'
                }`}>Save 20%</span>
              </button>
            </div>

            {/* Currency toggle */}
            <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white/[0.03] border border-white/10">
              <button
                onClick={() => setCurrency('USD')}
                data-testid="currency-toggle-usd"
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  currency === 'USD' ? 'bg-emerald-500 text-[#050B18]' : 'text-slate-300 hover:text-white'
                }`}
              >
                USD
              </button>
              <button
                onClick={() => setCurrency('INR')}
                data-testid="currency-toggle-inr"
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  currency === 'INR' ? 'bg-emerald-500 text-[#050B18]' : 'text-slate-300 hover:text-white'
                }`}
              >
                INR ₹
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing cards */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {loading ? (
            <div className="text-center text-slate-400 py-16" data-testid="pricing-loading">Loading plans…</div>
          ) : error ? (
            <div className="text-center text-red-300 py-16" data-testid="pricing-error">{error}</div>
          ) : plans.length === 0 ? (
            <div className="text-center text-slate-400 py-16" data-testid="pricing-empty">No plans available yet. Contact sales for a custom quote.</div>
          ) : (
            <div className={`grid grid-cols-1 md:grid-cols-2 ${gridCols} gap-5`} data-testid="pricing-cards">
              {plans.map((t) => {
                const p = price(t);
                const testId = `tier-${t.id}`;
                return (
                  <div
                    key={t.id}
                    data-testid={testId}
                    className={`relative rounded-2xl p-6 flex flex-col transition-all duration-300 ${
                      t.popular
                        ? 'bg-gradient-to-b from-emerald-500/[0.08] to-transparent border-2 border-emerald-500/40 shadow-xl shadow-emerald-500/10 lg:-translate-y-4'
                        : 'bg-white/[0.02] border border-white/5 hover:border-white/10'
                    }`}
                  >
                    {t.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-emerald-500 text-[#050B18] text-xs font-bold uppercase tracking-wider">
                        Most Popular
                      </div>
                    )}

                    <div className="mb-5">
                      <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{t.name}</h3>
                      <p className="text-sm text-slate-400 leading-snug min-h-[2.5rem]">{t.tagline}</p>
                    </div>

                    <div className="mb-5">
                      <div className="flex items-baseline gap-1">
                        <span className={`font-black text-white tracking-tight ${p.isCustom ? 'text-3xl' : 'text-4xl'}`} style={{ fontFamily: 'Outfit, sans-serif' }}>{p.display}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-1.5">{p.sub}</div>
                    </div>

                    <Link
                      to="/contact"
                      data-testid={`${testId}-cta`}
                      className={`block text-center px-4 py-3 rounded-lg font-semibold text-sm mb-6 transition-all ${
                        t.popular
                          ? 'bg-emerald-500 text-[#050B18] hover:bg-emerald-400 shadow-lg shadow-emerald-500/25'
                          : 'bg-white/[0.05] text-white border border-white/10 hover:border-white/20 hover:bg-white/[0.08]'
                      }`}
                    >
                      {t.cta || `Start with ${t.name}`}
                    </Link>

                    <ul className="space-y-2.5 text-sm">
                      {(t.features || []).map((h, i) => (
                        <li key={i} className="flex gap-2.5">
                          <CheckCircle
                            size={16}
                            weight="fill"
                            className={`flex-shrink-0 mt-0.5 ${t.popular ? 'text-emerald-400' : 'text-emerald-500/70'}`}
                          />
                          <span className="text-slate-400">{h}</span>
                        </li>
                      ))}
                      {(t.features || []).length === 0 && (
                        <li className="text-xs text-slate-600 italic">No features assigned yet.</li>
                      )}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && plans.length > 0 && (
            <p className="text-center text-xs text-slate-500 mt-10 flex items-center justify-center gap-2">
              <Info size={14} />
              All plans include a 14-day free trial. No credit card required. Cancel anytime.
            </p>
          )}
        </div>
      </section>

      {/* Add-Ons section */}
      {!loading && addons.length > 0 && (
        <section className="py-20 border-t border-white/5 bg-gradient-to-b from-emerald-500/[0.02] to-transparent" data-testid="addons-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs font-semibold uppercase tracking-wider mb-4">
                <Plus size={14} weight="bold" /> Add-ons
              </div>
              <h2 className="text-3xl lg:text-5xl font-black tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Bolt on <span className="text-emerald-400">just what you need</span>
              </h2>
              <p className="text-slate-400 text-base lg:text-lg">
                Every add-on is a full module we already ship. Buy any add-on on any plan — Starter included. All add-ons are <span className="text-emerald-300 font-semibold">billed annually</span>.
                Pro plan gets <span className="text-emerald-300 font-semibold">20% off</span>, Enterprise <span className="text-emerald-300 font-semibold">40% off</span>.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="addons-cards">
              {addons.map((a) => (
                <AddonCard key={a.id} addon={a} currency={currency} fmt={fmtMoney} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Feature comparison — dynamic table across every plan */}
      {!loading && plans.length > 0 && (
        <section className="py-20 border-t border-white/5 bg-[#0A1628]/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <h2 className="text-3xl lg:text-4xl font-black tracking-tight mb-2 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>Compare plans</h2>
            <p className="text-slate-400 text-center mb-12">All the details, side by side.</p>

            <div className="overflow-x-auto -mx-6 lg:mx-0" data-testid="pricing-comparison-table">
              <ComparisonTable plans={plans} />
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <h2 className="text-3xl lg:text-4xl font-black tracking-tight mb-12 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>Frequently asked</h2>
          <div className="space-y-4">
            {FAQ.map((qa, i) => (
              <details key={i} className="group rounded-xl bg-white/[0.02] border border-white/5 overflow-hidden" data-testid={`faq-${i}`}>
                <summary className="cursor-pointer p-5 flex items-center justify-between gap-4 hover:bg-white/[0.02]">
                  <span className="font-semibold text-white">{qa.q}</span>
                  <span className="text-emerald-400 text-xl group-open:rotate-45 transition-transform">+</span>
                </summary>
                <div className="px-5 pb-5 text-slate-400 text-sm leading-relaxed">{qa.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center px-6">
          <h2 className="text-4xl lg:text-5xl font-black tracking-tight mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Not sure which plan? <span className="text-emerald-400">Let&apos;s talk.</span>
          </h2>
          <p className="text-lg text-slate-400 mb-10">
            Tell us about your team and we&apos;ll recommend the right tier + add-ons — plus map your existing approval flow onto Vooxy live in a 30-min call.
          </p>
          <Link
            to="/contact"
            data-testid="pricing-final-cta"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-lg bg-emerald-500 text-[#050B18] font-bold hover:bg-emerald-400 shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all"
          >
            Talk to sales
            <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

/**
 * AddonCard — compact card showing one add-on's price + inclusions. Pricing
 * model note (per_field_user / flat_per_tenant) is surfaced inline so a visitor
 * understands why some add-ons scale per user and others are flat.
 */
function AddonCard({ addon, currency, fmt }) {
  const usd = addon.yearly_price_usd || 0;
  const inr = addon.yearly_price_inr || Math.round(usd * USD_TO_INR);

  // `per_user` pricing surfaces the monthly figure first because that reads
  // more naturally for seat-based add-ons (same convention as the main
  // tier cards). Fall back to a USD×12 split if `monthly_price_*` is absent.
  const isPerUser = addon.pricing_model === 'per_user';
  const monthlyUsd = addon.monthly_price_usd || Math.round(usd / 12);
  const monthlyInr = addon.monthly_price_inr || Math.round(inr / 12);

  const priceDisplay = isPerUser
    ? (currency === 'INR'
      ? `₹${monthlyInr.toLocaleString('en-IN')}`
      : `$${monthlyUsd}`)
    : (currency === 'INR'
      ? `₹${inr.toLocaleString('en-IN')}`
      : `$${usd}`);

  const modelLabel = (() => {
    switch (addon.pricing_model) {
      case 'per_field_user':
        return 'per field user / year';
      case 'per_user':
        return `per user / month · billed annually at ${currency === 'INR' ? `₹${inr.toLocaleString('en-IN')}` : `$${usd}`}/user/year`;
      case 'flat_per_tenant':
        return addon.flat_user_limit
          ? `per year · up to ${addon.flat_user_limit} users`
          : 'per year · flat';
      default:
        return 'per year';
    }
  })();

  return (
    <div
      data-testid={`addon-${addon.id}`}
      className="relative rounded-2xl p-6 bg-white/[0.02] border border-white/5 hover:border-emerald-500/30 transition-all duration-300 flex flex-col"
    >
      {addon.badge && (
        <div
          data-testid={`addon-${addon.id}-badge`}
          className="absolute -top-3 left-5 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500 text-[#050B18] text-[10px] font-bold uppercase tracking-wider shadow-lg shadow-emerald-500/30"
        >
          {addon.badge}
        </div>
      )}
      <h3 className="text-lg font-bold text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{addon.name}</h3>
      <p className="text-xs text-slate-400 mb-4 min-h-[2rem]">{addon.tagline}</p>

      <div className="mb-4">
        <div className="flex items-baseline gap-1">
          <span className="text-3xl font-black text-white tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{priceDisplay}</span>
        </div>
        <div className="text-[11px] text-slate-500 mt-1">{modelLabel}</div>
        {addon.badge_sub && (
          <div className="text-[11px] text-emerald-300 font-medium mt-0.5">{addon.badge_sub}</div>
        )}
        {addon.flat_user_limit && addon.overage_price_usd && (
          <div className="text-[11px] text-slate-600 mt-0.5">
            + {currency === 'INR' ? `₹${addon.overage_price_inr}` : `$${addon.overage_price_usd}`} per extra user
          </div>
        )}
      </div>

      <ul className="space-y-2 text-xs mb-4">
        {(addon.features || []).map((f, i) => (
          <li key={i} className="flex gap-2">
            <CheckCircle size={13} weight="fill" className="flex-shrink-0 mt-0.5 text-emerald-500/70" />
            <span className="text-slate-400">{f}</span>
          </li>
        ))}
      </ul>

      <Link
        to="/contact"
        data-testid={`addon-${addon.id}-cta`}
        className="mt-auto block text-center px-4 py-2.5 rounded-lg bg-white/[0.03] text-white border border-white/10 hover:border-emerald-500/40 hover:bg-emerald-500/5 text-xs font-semibold transition-all"
      >
        Add to my plan
      </Link>
    </div>
  );
}

/**
 * Build a full feature × plan matrix from the plans response.
 * `plans[i].features` is a flat list of names — same feature name may appear
 * in multiple plans. We union all feature names, preserve their order-of-
 * appearance across plans, then render check-or-x cells.
 */
function ComparisonTable({ plans }) {
  const seen = new Set();
  const allFeatures = [];
  for (const p of plans) {
    for (const f of p.features || []) {
      if (!seen.has(f)) { seen.add(f); allFeatures.push(f); }
    }
  }
  const has = (plan, feat) => (plan.features || []).includes(feat);
  return (
    <table className="w-full min-w-[720px] text-sm">
      <thead>
        <tr className="border-b border-white/10">
          <th className="text-left py-4 px-4 font-semibold text-slate-300"> </th>
          {plans.map((p) => (
            <th key={p.id} className="py-4 px-4 font-bold text-white text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>{p.name}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {allFeatures.map((feat, i) => (
          <tr key={i} className="border-b border-white/5 hover:bg-white/[0.01]">
            <td className="py-3 px-4 text-slate-300">{feat}</td>
            {plans.map((p) => (
              <td key={p.id} className="py-3 px-4 text-center">
                {has(p, feat)
                  ? <CheckCircle size={18} weight="fill" className="inline text-emerald-400" />
                  : <X size={18} className="inline text-slate-600" />}
              </td>
            ))}
          </tr>
        ))}
        {allFeatures.length === 0 && (
          <tr><td colSpan={plans.length + 1} className="py-10 text-center text-slate-500">No features listed yet.</td></tr>
        )}
      </tbody>
    </table>
  );
}

const FAQ = [
  {
    q: 'How is a "user" counted?',
    a: 'Any active team member with a Vooxy login — Sales Executive, Manager, VP, President, or Admin. Invited-but-inactive users don\'t count. You can add and remove users any time; billing prorates automatically.',
  },
  {
    q: 'Do you offer a free trial?',
    a: '14 days on any plan, no credit card required. You\'ll get your own tenant workspace and full access to every feature in the tier you pick. No feature crippling.',
  },
  {
    q: 'Can I switch plans later?',
    a: 'Yes — up or down, any time. Upgrades take effect immediately. Downgrades apply at the next billing cycle so you don\'t lose data mid-month.',
  },
  {
    q: 'How do add-ons work?',
    a: 'Add-ons are billed annually and can be stacked on any plan — including Starter. Mid-cycle purchases are prorated. Pro plan customers get 20% off add-ons; Enterprise bundles all add-ons at 40% off.',
  },
  {
    q: 'Is my data isolated from other customers?',
    a: 'Yes. Vooxy uses per-tenant database scoping at the query layer — every read and write is auto-filtered by tenant_id. We ship regression tests that specifically probe for cross-tenant leaks on every deploy.',
  },
  {
    q: 'Do you support Indian GST properly?',
    a: 'Vooxy was built for Indian manufacturing first. IGST/CGST/SGST auto-split by state, GSTIN format validation, and a full exemption request workflow with President-level approval (via the Compliance add-on). It\'s not an afterthought.',
  },
  {
    q: 'What happens to my data if I cancel?',
    a: 'You get 30 days of read-only access to export everything (CSV + PDF archives). After that, your tenant is permanently deleted from our database — including audit logs.',
  },
  {
    q: 'Do you have a discount for non-profits or startups?',
    a: 'Yes — 50% off Starter/Growth for registered non-profits and pre-seed startups (< 10 employees). Reach out via the contact form.',
  },
];
